import React, { useState, useContext } from "react";
import { Container } from "../styled.ts";
import { Typography } from "../typography/index.tsx";
import { useMobile } from "../../hooks/isMobile.tsx";
import { API_URLS } from "../../utils/apiConfig.ts";
import { subscriberContext } from "../../context/subscriberContext.tsx";

type DiscountType = 'percentage' | 'fixed';

interface CouponAppliedPayload {
  code: string;
  discount: number;
  discountType: DiscountType;
  discountedAmount?: number; // final integer from backend
}

interface CouponModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyCoupon: (payload: CouponAppliedPayload | null) => void;
  planName: string;
  originalAmount: number;
  billingCycle?: "Monthly" | "Yearly";
  appliedCoupon?: {
    code: string;
    discount: number;
    discountType: DiscountType;
    discountedAmount?: number;
  } | null;
  onProceedPayment?: () => void;
}

function CouponModal({ 
  isOpen, 
  onClose, 
  onApplyCoupon, 
  planName, 
  originalAmount,
  billingCycle = "Monthly",
  appliedCoupon,
  onProceedPayment
}: CouponModalProps) {
  const { isMobile } = useMobile();
  const { subscriberDetails } = useContext(subscriberContext);
  const [couponCode, setCouponCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [startY, setStartY] = useState(0);

  if (!isOpen) return null;

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isMobile) {
      setStartY(e.touches[0].clientY);
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (isMobile) {
      const endY = e.changedTouches[0].clientY;
      const diff = startY - endY;
      if (diff > 100) {
        onClose();
      }
    }
  };

  const finalAmount = appliedCoupon?.discountedAmount != null ? appliedCoupon.discountedAmount : originalAmount;

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      setError("Please enter a coupon code");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("tokn");
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const requestBody = {
        couponCode: couponCode.toUpperCase(),
        planName: planName,
        billingCycle: billingCycle,
        userId: subscriberDetails?.phoneNumber || "",
        orderAmount: originalAmount
      };

      const resp = await fetch(API_URLS.COUPON_VALIDATE, {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody)
      });

      const data = await resp.json();

      if (data.valid) {
        const discountType: DiscountType = (data.discountType || 'fixed').toLowerCase() === 'percentage' ? 'percentage' : 'fixed';
        const discountValue: number = Number(data.discountValue) || 0;
        const discountedAmount: number | undefined = Number.isFinite(Number(data.discountedAmount)) ? Number(data.discountedAmount) : undefined;
        onApplyCoupon({
          code: couponCode.toUpperCase(),
          discount: discountValue,
          discountType,
          discountedAmount
        });
        setCouponCode("");
      } else {
        setError(data.message || "Invalid coupon code");
      }
    } catch (err) {
      setError("Failed to validate coupon. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    onApplyCoupon(null);
    setCouponCode("");
    setError("");
  };

  return (
    <Container
      style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(0, 0, 0, 0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? "20px" : "40px" }}
      onClick={onClose}
    >
      <Container
        column
        gap={isMobile ? "16px" : "20px"}
        background="#fff"
        padding={isMobile ? "16px" : "24px"}
        radius={isMobile ? "16px 16px 0 0" : "20px"}
        style={{ maxWidth: isMobile ? "100%" : "480px", width: "100%", maxHeight: isMobile ? "90vh" : "80vh", overflow: "auto", margin: isMobile ? "0" : "0", position: isMobile ? "fixed" : "relative", bottom: isMobile ? "0" : "auto", left: isMobile ? "0" : "auto", right: isMobile ? "0" : "auto" }}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        {isMobile && (
          <Container justifyContent="center" style={{ paddingTop: "8px" }}>
            <Container background="#ddd" style={{ width: "40px", height: "4px", borderRadius: "2px" }} />
          </Container>
        )}

        <Container justifyContent="space-between" alignItems="center">
          <Typography tag="h2" fontFamily="Neue Montreal" fontSize={isMobile ? "18px" : "22px"} fontWeight={600} color="#202020">Apply Coupon Code</Typography>
          <Container style={{ cursor: "pointer", padding: isMobile ? "8px" : "5px", minWidth: isMobile ? "32px" : "24px", minHeight: isMobile ? "32px" : "24px", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
            <Typography fontSize={isMobile ? "20px" : "24px"} color="#666">×</Typography>
          </Container>
        </Container>

        <Container background="#f8f8f8" padding={isMobile ? "12px" : "15px"} radius={isMobile ? "8px" : "10px"} column gap={isMobile ? "4px" : "5px"}>
          <Typography fontFamily="Neue Montreal" fontSize={isMobile ? "13px" : "14px"} fontWeight={500} color="#666">{planName} Plan</Typography>
          <Container alignItems="baseline" gap={isMobile ? "6px" : "8px"}>
            {appliedCoupon && appliedCoupon.discountedAmount != null ? (
              <>
                <Typography fontFamily="Neue Montreal" fontSize={isMobile ? "16px" : "18px"} fontWeight={600} color="#202020">₹{finalAmount}</Typography>
                <span style={{ textDecoration: "line-through", color: "#666", fontFamily: "Neue Montreal", fontSize: "14px", fontWeight: 400 }}>₹{originalAmount}</span>
                <Typography fontFamily="Neue Montreal" fontSize="14px" fontWeight={500} color="#5395FF">-{appliedCoupon.discount}{appliedCoupon.discountType === 'percentage' ? '%' : ''}</Typography>
              </>
            ) : (
              <Typography fontFamily="Neue Montreal" fontSize={isMobile ? "16px" : "18px"} fontWeight={600} color="#202020">₹{originalAmount}</Typography>
            )}
          </Container>
        </Container>

        {appliedCoupon && appliedCoupon.discountedAmount != null && (
          <Container background="#E8F0FF" padding="12px 15px" radius="8px" justifyContent="space-between" alignItems="center">
            <Container alignItems="center" gap="8px">
              <Typography fontFamily="Neue Montreal" fontSize="14px" fontWeight={500} color="#5395FF">✓ {appliedCoupon.code}</Typography>
              <Typography fontFamily="Neue Montreal" fontSize="12px" fontWeight={400} color="#666">Applied</Typography>
            </Container>
            <Container style={{ cursor: "pointer", padding: "5px" }} onClick={handleRemoveCoupon}>
              <Typography fontSize="16px" color="#666">×</Typography>
            </Container>
          </Container>
        )}

        {!appliedCoupon && (
          <Container column gap={isMobile ? "8px" : "10px"}>
            <Typography fontFamily="Neue Montreal" fontSize={isMobile ? "13px" : "14px"} fontWeight={500} color="#202020">Enter Coupon Code</Typography>
            <Container gap={isMobile ? "8px" : "10px"} column={isMobile}>
              <input type="text" value={couponCode} onChange={(e) => setCouponCode(e.target.value.toUpperCase())} placeholder="e.g., WELCOME10" style={{ flex: 1, padding: isMobile ? "14px 12px" : "12px 15px", border: "1px solid #ddd", borderRadius: isMobile ? "6px" : "8px", fontSize: isMobile ? "16px" : "14px", fontFamily: "Neue Montreal", outline: "none", minHeight: isMobile ? "48px" : "auto" }} onKeyPress={(e) => e.key === 'Enter' && handleApplyCoupon()} />
              <Container background={isLoading ? "#ccc" : "linear-gradient(165.56deg, #5395FF 39.75%, #D69DFF 58.69%)"} padding={isMobile ? "14px 20px" : "12px 20px"} radius={isMobile ? "6px" : "8px"} style={{ cursor: isLoading ? "not-allowed" : "pointer", minHeight: isMobile ? "48px" : "auto", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={isLoading ? undefined : handleApplyCoupon}>
                <Typography fontFamily="Neue Montreal" fontSize={isMobile ? "15px" : "14px"} fontWeight={500} color="#fff">{isLoading ? "Applying..." : "Apply"}</Typography>
              </Container>
            </Container>
          </Container>
        )}

        {error && (
          <Container background="#FFE8E8" padding="10px 15px" radius="8px">
            <Typography fontFamily="Neue Montreal" fontSize="12px" fontWeight={400} color="#FF4444">{error}</Typography>
          </Container>
        )}

        <Container gap={isMobile ? "8px" : "10px"} justifyContent="flex-end" column={isMobile}>
          <Container background="#f0f0f0" padding={isMobile ? "14px 20px" : "12px 20px"} radius={isMobile ? "6px" : "8px"} style={{ cursor: "pointer", minHeight: isMobile ? "48px" : "auto", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
            <Typography fontFamily="Neue Montreal" fontSize={isMobile ? "15px" : "14px"} fontWeight={500} color="#666">{appliedCoupon && appliedCoupon.discountedAmount != null ? "Continue" : "Cancel"}</Typography>
          </Container>
          {appliedCoupon && appliedCoupon.discountedAmount != null && (
            <Container background="linear-gradient(165.56deg, #5395FF 39.75%, #D69DFF 58.69%)" padding={isMobile ? "14px 20px" : "12px 20px"} radius={isMobile ? "6px" : "8px"} style={{ cursor: "pointer", minHeight: isMobile ? "48px" : "auto", display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => onProceedPayment && onProceedPayment()}>
              <Typography fontFamily="Neue Montreal" fontSize={isMobile ? "15px" : "14px"} fontWeight={500} color="#fff">Proceed to Payment</Typography>
            </Container>
          )}
        </Container>
      </Container>
    </Container>
  );
}

export default CouponModal;
